import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchScholarshipsByStatus,
  updateScholarship,
} from "../store/slices/scholarshipSlice";
import { useSnackbar } from "notistack";
import { CheckCircle, XCircle, Search, Award, ChevronDown } from "lucide-react";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";

const ScholarshipList = () => {
  const dispatch = useDispatch();
  const { scholarships, loading, error } = useSelector(
    (state) => state.scholarship
  );
  const { enqueueSnackbar } = useSnackbar();
  const [searchTerm, setSearchTerm] = useState("");
  const [updatedScholarships, setUpdatedScholarships] = useState(scholarships);
  const { theme, gradient } = useSidebarContext();

  useEffect(() => {
    dispatch(fetchScholarshipsByStatus("Pending"));
  }, [dispatch]);

  useEffect(() => {
    setUpdatedScholarships(scholarships);
  }, [scholarships]);

  const handleShortlist = (id, name) => {
    dispatch(updateScholarship({ id, statusUpdate: { status: "Shortlisted" } }))
      .then(() => {
        setUpdatedScholarships((prevScholarships) =>
          prevScholarships.filter((scholarship) => scholarship._id !== id)
        );
        enqueueSnackbar(`${name} has been shortlisted!`, {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
      })
      .catch((error) => {
        enqueueSnackbar(error.message || "Failed to shortlist scholarship", {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
      });
  };

  const handleReject = (id, name) => {
    dispatch(updateScholarship({ id, statusUpdate: { status: "Rejected" } }))
      .then(() => {
        setUpdatedScholarships((prevScholarships) =>
          prevScholarships.filter((scholarship) => scholarship._id !== id)
        );
        enqueueSnackbar(`${name} has been rejected`, {
          variant: "info",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
      })
      .catch((error) => {
        enqueueSnackbar(error.message || "Failed to reject scholarship", {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
      });
  };

  const filteredScholarships = updatedScholarships.filter(
    (scholarship) =>
      scholarship.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      scholarship.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading)
    return (
      <div
        className={`flex items-center justify-center min-h-screen ${gradient}`}
      >
        <div className="relative">
          <div
            className={`w-16 h-16 border-t-4 border-b-4 ${theme.tailwind.border25} rounded-full animate-spin`}
          ></div>
          <div
            className={`w-16 h-16 border-t-4 border-b-4 ${theme.tailwind.border11} rounded-full animate-spin absolute top-0 left-0`}
            style={{ animationDelay: "-0.3s" }}
          ></div>
        </div>
      </div>
    );

  if (error)
    return (
      <div className={`min-h-screen ${gradient} p-8`}>
        <div className="max-w-7xl mx-auto bg-red-500/10 border border-red-500/50 rounded-2xl p-8 backdrop-blur-lg shadow-2xl">
          <div className="flex items-center space-x-4">
            <XCircle className="w-8 h-8 text-red-400" />
            <p className="text-red-400 text-lg">
              {error?.message || "An error occurred"}
            </p>
          </div>
        </div>
      </div>
    );

  return (
    <div className={`${gradient} p-8 rounded-xl shadow-lg`}>
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Award className={`w-10 h-10 ${theme.tailwind.text9}`} />
            <h2
              className={`text-4xl font-bold ${theme.tailwind.text9} tracking-tight`}
            >
              Pending Scholarships
            </h2>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search scholarships..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-64 px-4 py-2 pl-11 rounded-xl ${theme.tailwind.bg3} border ${theme.tailwind.border13} 
                         ${theme.tailwind.focus1} focus:ring-2 ${theme.tailwind.focusRing6} focus:outline-none
                         ${theme.tailwind.texting1} ${theme.tailwind.placeHolder3} transition-all duration-300`}
            />
            <Search
              className={`w-5 h-5 ${theme.tailwind.texting2} absolute left-3 top-2.5`}
            />
          </div>
        </div>

        {/* Main Content */}
        <div
          className={`relative rounded-2xl border ${theme.tailwind.border2} ${theme.tailwind.bg36} backdrop-blur-xl 
                        shadow-[0_0_40px_rgba(45,212,191,0.1)] overflow-hidden`}
        >
          <div
            className={`absolute inset-0 ${theme.tailwind.BgBottom5} pointer-events-none`}
          ></div>

          {filteredScholarships.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`border-b ${theme.tailwind.border2}`}>
                    <th
                      className={`px-8 py-5 text-left text-sm font-medium ${theme.tailwind.text9} uppercase tracking-wider`}
                    >
                      <div className="flex items-center space-x-2">
                        <span>Name</span>
                        <ChevronDown className="w-4 h-4" />
                      </div>
                    </th>
                    <th
                      className={`px-8 py-5 text-left text-sm font-medium ${theme.tailwind.text9} uppercase tracking-wider`}
                    >
                      Email
                    </th>
                    <th
                      className={`px-8 py-5 text-left text-sm font-medium ${theme.tailwind.text9} uppercase tracking-wider`}
                    >
                      Status
                    </th>
                    <th
                      className={`px-8 py-5 text-left text-sm font-medium ${theme.tailwind.text9} uppercase tracking-wider`}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${theme.tailwind.divide1}`}>
                  {filteredScholarships.map((scholarship) => (
                    <tr
                      key={scholarship._id}
                      className={`${theme.tailwind.bgHover19} transition-colors duration-200`}
                    >
                      <td className="px-8 py-6">
                        <div className="flex items-center space-x-3">
                          <div
                            className={`w-10 h-10 rounded-full ${theme.tailwind.bgGradient21}
                                      flex items-center justify-center text-white font-bold`}
                          >
                            {scholarship.name.charAt(0)}
                          </div>
                          <span
                            className={`${theme.tailwind.texting1} font-medium`}
                          >
                            {scholarship.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <span className={`${theme.tailwind.texting1}`}>
                          {scholarship.email}
                        </span>
                      </td>
                      <td className="px-8 py-6">
                        <span
                          className={`
                        inline-flex items-center px-4 py-2 rounded-full text-sm font-medium space-x-2
                        ${
                          scholarship.status === "Pending"
                            ? "bg-yellow-500/20 text-yellow-400 ring-1 ring-yellow-400/50"
                            : ""
                        }
                        ${
                          scholarship.status === "Shortlisted"
                            ? "bg-green-500/20 text-green-400 ring-1 ring-green-400/50"
                            : ""
                        }
                        ${
                          scholarship.status === "Rejected"
                            ? "bg-red-500/20 text-red-400 ring-1 ring-red-400/50"
                            : ""
                        }
                      `}
                        >
                          <span
                            className={`w-2 h-2 rounded-full
                          ${
                            scholarship.status === "Pending"
                              ? "bg-yellow-400"
                              : ""
                          }
                          ${
                            scholarship.status === "Shortlisted"
                              ? "bg-green-400"
                              : ""
                          }
                          ${
                            scholarship.status === "Rejected"
                              ? "bg-red-400"
                              : ""
                          }
                        `}
                          ></span>
                          <span>{scholarship.status}</span>
                        </span>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex space-x-4">
                          {scholarship.status !== "Shortlisted" &&
                            scholarship.status !== "Rejected" && (
                              <button
                                onClick={() =>
                                  handleShortlist(
                                    scholarship._id,
                                    scholarship.name
                                  )
                                }
                                className={`inline-flex items-center px-4 py-2 rounded-lg ${theme.tailwind.bg1} ${theme.tailwind.text9} 
                                     ${theme.tailwind.bgHover28} ${theme.tailwind.active1} transition-all duration-200
                                     focus:outline-none focus:ring-2 ${theme.tailwind.focusRing1} focus:ring-offset-2 
                                     ${theme.tailwind.focusRing9} space-x-2 shadow-lg ${theme.tailwind.shadow5}`}
                              >
                                <CheckCircle className="w-4 h-4" />
                                <span>Shortlist</span>
                              </button>
                            )}
                          {scholarship.status !== "Rejected" &&
                            scholarship.status !== "Shortlisted" && (
                              <button
                                onClick={() =>
                                  handleReject(
                                    scholarship._id,
                                    scholarship.name
                                  )
                                }
                                className="inline-flex items-center px-4 py-2 rounded-lg bg-red-500/20 text-red-300 
                                     hover:bg-red-500/30 active:bg-red-500/40 transition-all duration-200
                                     focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:ring-offset-2 
                                     focus:ring-offset-slate-900 space-x-2 shadow-lg shadow-red-500/10"
                              >
                                <XCircle className="w-4 h-4" />
                                <span>Reject</span>
                              </button>
                            )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`${theme.tailwind.shadow5} mb-4`}
              >
                <rect width="20" height="16" x="2" y="4" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
              <h3
                className={`text-xl font-medium ${theme.tailwind.texting1} mb-2`}
              >
                No scholarships found
              </h3>
              <p className={`${theme.tailwind.texting2} text-center max-w-md`}>
                {searchTerm
                  ? `No scholarships matching "${searchTerm}" were found. Try adjusting your search.`
                  : "There are no pending scholarships at the moment."}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScholarshipList;
